import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '@cleanpro/common-ui';
import { useSetPageMeta } from '../../../hooks/usePageMeta';
import type { ObservabilityMetrics, AdNetwork } from '../types';
import { fetchObservabilityMetrics } from '../api';

const NETWORK_LABELS: Record<AdNetwork, string> = {
  meta: 'Meta',
  google_ads: 'Google Ads',
  google_lsa: 'Google LSA',
};

function StatCard({
  label,
  value,
  subtext,
  status,
}: {
  label: string;
  value: string | number;
  subtext?: string;
  status?: 'ok' | 'warn' | 'error';
}) {
  const statusColor =
    status === 'error'
      ? 'text-red-700'
      : status === 'warn'
        ? 'text-yellow-700'
        : 'text-gray-900';

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${statusColor}`}>{value}</p>
      {subtext && <p className="mt-1 text-xs text-gray-400">{subtext}</p>}
    </div>
  );
}

function CpaqTable({ metrics }: { metrics: ObservabilityMetrics }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800">CPAQ Trailing 7d — All Providers</h3>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
          <tr>
            <th className="px-4 py-3 text-left">Network</th>
            <th className="px-4 py-3 text-right">Avg CPAQ (7d)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {metrics.cpaqByNetwork.map((row) => (
            <tr key={row.network}>
              <td className="px-4 py-3 font-medium text-gray-700">{NETWORK_LABELS[row.network]}</td>
              <td className="px-4 py-3 text-right">
                {row.cpaqTrailing7d !== null
                  ? formatCurrency(row.cpaqTrailing7d)
                  : <span className="text-gray-400">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DailySpendTable({ metrics }: { metrics: ObservabilityMetrics }) {
  const rows = metrics.dailySpendByNetwork.slice(-14); // last 14 days

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800">Daily Spend by Network (last 14 days)</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-right">Meta</th>
              <th className="px-4 py-3 text-right">Google Ads</th>
              <th className="px-4 py-3 text-right">LSA</th>
              <th className="px-4 py-3 text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-4 text-center text-gray-400 text-xs italic">No spend data</td>
              </tr>
            ) : (
              rows.map((r) => {
                const total = r.meta + r.googleAds + r.googleLsa;
                return (
                  <tr key={r.date} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-600">{r.date}</td>
                    <td className="px-4 py-2 text-right">{r.meta > 0 ? formatCurrency(r.meta) : <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-2 text-right">{r.googleAds > 0 ? formatCurrency(r.googleAds) : <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-2 text-right">{r.googleLsa > 0 ? formatCurrency(r.googleLsa) : <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-2 text-right font-medium">{formatCurrency(total)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AllocatorActivityTable({ metrics }: { metrics: ObservabilityMetrics }) {
  const rows = metrics.allocatorDecisionsPerDay.slice(-14);
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800">Allocator Decisions per Day (last 14 days)</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-right">Decisions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={2} className="px-4 py-4 text-center text-gray-400 text-xs italic">No allocator history</td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.date} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-600">{r.date}</td>
                  <td className="px-4 py-2 text-right font-medium">{r.count}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ObservabilityPage() {
  const [metrics, setMetrics] = useState<ObservabilityMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useSetPageMeta({
    title: 'Smart Growth Observability',
    description: 'Platform-wide spend, CPAQ, allocator activity, and LSA sync drift',
  });

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchObservabilityMetrics();
      setMetrics(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load metrics');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-24 animate-pulse rounded-lg bg-gray-100" />
        ))}
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-sm text-red-700">{error ?? 'Metrics unavailable'}</p>
        <button onClick={load} className="mt-3 text-sm text-red-600 underline hover:text-red-800">
          Retry
        </button>
      </div>
    );
  }

  const { drift } = metrics.lsaChargeDrift;
  const driftStatus = Math.abs(drift) === 0 ? 'ok' : Math.abs(drift) <= 2 ? 'warn' : 'error';
  const errorStatus = metrics.googleAdsErrorCount === 0 ? 'ok' : metrics.googleAdsErrorCount < 5 ? 'warn' : 'error';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/growth-ops" className="text-xs text-gray-500 hover:text-gray-700 mb-1 inline-flex items-center gap-1">
          ← Growth Ops
        </Link>
        <h1 className="text-lg font-semibold text-gray-900">Smart Growth Observability</h1>
        <p className="text-sm text-gray-500">Platform-wide metrics across all providers and networks</p>
      </div>

      {/* Health cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Google Ads API Errors"
          value={metrics.googleAdsErrorCount}
          subtext="Trailing 7 days"
          status={errorStatus}
        />
        <StatCard
          label="LSA Charge Drift"
          value={drift === 0 ? 'None' : `${drift > 0 ? '+' : ''}${drift}`}
          subtext={`${metrics.lsaChargeDrift.leadAttributionCount} leads vs ${metrics.lsaChargeDrift.walletChargeCount} wallet charges (7d)`}
          status={driftStatus}
        />
        <StatCard
          label="Allocator Runs (30d)"
          value={metrics.allocatorDecisionsPerDay.reduce((s, r) => s + r.count, 0)}
          subtext={`Across ${metrics.allocatorDecisionsPerDay.length} days`}
        />
        <StatCard
          label="Total 30d Spend"
          value={formatCurrency(
            metrics.dailySpendByNetwork.reduce((s, r) => s + r.meta + r.googleAds + r.googleLsa, 0)
          )}
          subtext="All networks combined"
        />
      </div>

      {/* LSA drift alert */}
      {driftStatus !== 'ok' && (
        <div className={`rounded-lg border p-4 ${driftStatus === 'error' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`}>
          <p className={`text-sm font-medium ${driftStatus === 'error' ? 'text-red-800' : 'text-yellow-800'}`}>
            LSA charge sync drift detected: {drift} row(s) unreconciled
          </p>
          <p className={`text-xs mt-1 ${driftStatus === 'error' ? 'text-red-600' : 'text-yellow-600'}`}>
            See runbook: <code className="font-mono">docs/runbooks/growth-lsa-charge-sync-drift.md</code>
          </p>
        </div>
      )}

      {/* Google Ads error alert */}
      {errorStatus !== 'ok' && (
        <div className={`rounded-lg border p-4 ${errorStatus === 'error' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`}>
          <p className={`text-sm font-medium ${errorStatus === 'error' ? 'text-red-800' : 'text-yellow-800'}`}>
            {metrics.googleAdsErrorCount} Google Ads API error{metrics.googleAdsErrorCount !== 1 ? 's' : ''} in last 7 days
          </p>
          <p className={`text-xs mt-1 ${errorStatus === 'error' ? 'text-red-600' : 'text-yellow-600'}`}>
            See runbook: <code className="font-mono">docs/runbooks/growth-google-ads-mcc-suspended.md</code>
          </p>
        </div>
      )}

      <CpaqTable metrics={metrics} />
      <DailySpendTable metrics={metrics} />
      <AllocatorActivityTable metrics={metrics} />
    </div>
  );
}
